import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowLeft from '@jwp/ott-theme/assets/icons/arrow_backward.svg?react';
import WarningIcon from '@jwp/ott-theme/assets/icons/warning.svg?react';

import Radio from '../Radio/Radio';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { useTheme } from '../../../../../ThemeContext';
import { modalURLFromLocation } from '../../utils/location';

import styles from './DeleteAccountReason.module.scss';

const DeleteAccountReason = () => {
  const { t } = useTranslation(['user', 'common', 'account']);
  const { universalStyles } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedReason, setSelectedReason] = useState<string>('');
  const [otherReasonText, setOtherReasonText] = useState<string>('');

  const reasons = [
    {
      value: t('user:account.delete_account.reasons.another_account', { defaultValue: 'I already have another account' }),
      label: t('user:account.delete_account.reasons.another_account', { defaultValue: 'I already have another account' }),
    },
    {
      value: t('user:account.delete_account.reasons.another_tool', { defaultValue: 'My organization uses another tool' }),
      label: t('user:account.delete_account.reasons.another_tool', { defaultValue: 'My organization uses another tool' }),
    },
    {
      value: t('user:account.delete_account.reasons.technical_issues', { defaultValue: 'I experienced technical issues' }),
      label: t('user:account.delete_account.reasons.technical_issues', { defaultValue: 'I experienced technical issues' }),
    },
    {
      value: t('user:account.delete_account.reasons.missing_features', { defaultValue: 'Missing features I need' }),
      label: t('user:account.delete_account.reasons.missing_features', { defaultValue: 'Missing features I need' }),
    },
    {
      value: t('user:account.delete_account.reasons.privacy_concerns', { defaultValue: 'Privacy concerns' }),
      label: t('user:account.delete_account.reasons.privacy_concerns', { defaultValue: 'Privacy concerns' }),
    },
    {
      value: 'other',
      label: t('user:account.delete_account.reasons.other', { defaultValue: 'Other (Please specify)' }),
    },
  ];

  useEffect(() => {
    const storedReason = localStorage.getItem('delete_account_reason');
    if (storedReason) {
      const isStandardReason = reasons.some(reason => reason.value === storedReason);
      if (isStandardReason) {
        setSelectedReason(storedReason);
      } else {
        setSelectedReason('other');
        setOtherReasonText(storedReason);
      }
    }
  }, []);

  const handleReasonSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReason) { return; }
    if (selectedReason === 'other' && !otherReasonText.trim()) { return; }

    const finalReason = selectedReason === 'other' ? otherReasonText.trim() : selectedReason;

    // Securely pass reason via localStorage
    localStorage.setItem('delete_account_reason', finalReason);

    // Open the existing delete account modal with mapped reason in state
    navigate(
      modalURLFromLocation(location, 'delete-account'),
      {replace: true}
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate('/u/my-account')} aria-label={t('common:back')}>
          <Icon icon={ArrowLeft} />
        </button>
        <h2 className={styles.title} style={universalStyles.primaryTextColor}>
          {t('user:account.delete_account.title')}
        </h2>
      </div>

      <form onSubmit={handleReasonSubmit} className={styles.form}>
        {/* Warning Banner */}
        <div className={styles.warningCard}>
          <div className={styles.warningIcon}>
            <Icon icon={WarningIcon} />
          </div>
          <div className={styles.warningContent}>
            <h4 className={styles.warningTitle}>
              {t('user:account.delete_account.warning_title', { defaultValue: 'Warning: This affects all Katon Products.' })}
            </h4>
            <p className={styles.warningBody}>
              {t('user:account.delete_account.warning_body', { 
                defaultValue: "Deleting your Katon account permanently removes your access and data across all Katon products connected through single sign-on. This action can't be undone." 
              })}
            </p>
          </div>
        </div>

        <div className={styles.reasonGroup}>
          <Radio
            name="deleteReason"
            value={selectedReason}
            values={reasons}
            onChange={(e) => {
              const val = e.target.value;
              setSelectedReason(val);
              if (val !== 'other') {
                setOtherReasonText('');
              }
            }}
            required
          />
        </div>

        {selectedReason === 'other' && (
          <div className={styles.otherInputWrapper}>
            <label htmlFor="delete-reason-other" className={styles.fieldLabel} style={universalStyles.primaryTextColor}>
              {t('user:account.delete_account.reason_label', { defaultValue: 'Reason For Deletion' })}
              <span className={styles.requiredAsterisk}>*</span>
            </label>
            <textarea
              id="delete-reason-other"
              className={styles.textarea}
              placeholder={t('user:account.delete_account.other_placeholder', { defaultValue: 'Please describe your reason' })}
              value={otherReasonText}
              onChange={(e) => setOtherReasonText(e.target.value)}
              rows={4}
              required
            />
          </div>
        )}

        <div className={styles.actions}>
          <Button
            type="submit"
            disabled={!selectedReason || (selectedReason === 'other' && !otherReasonText.trim())}
            color="primary"
            size="exlarge"
            variant="contained"
            label={t('common:submit', { defaultValue: 'Submit' })}
            className={styles.submitBtn}
            style={universalStyles.signUpButton}
            textStyle={universalStyles.signUpButtonText}
          />
        </div>
      </form>
    </div>
  );
};

export default DeleteAccountReason;
